// What each task action is called on its button and what it asks for (spec §5.2). Plain English
// for helpers (constitution §5.10).
import type { UserTaskAction } from '@cis/api-client';
import type { TaskWrite } from '../../api/tasks.ts';

export interface ActionInfo {
  readonly label: string;
  /** Typed text the action needs: a required reason or note, an optional note, or none. */
  readonly text: 'reason' | 'note' | 'optional-note' | null;
  readonly prompt: string;
  readonly done: string;
}

export const ACTION_INFO: Readonly<Record<UserTaskAction, ActionInfo>> = {
  start: { label: 'Start work', text: null, prompt: '', done: 'Task started.' },
  complete: { label: 'Mark as done', text: null, prompt: '', done: 'Task marked as done.' },
  verify: {
    label: 'Verify',
    text: 'optional-note',
    prompt: 'Note for the record (optional)',
    done: 'Task verified.',
  },
  'send-back': {
    label: 'Send back',
    text: 'reason',
    prompt: 'What needs to be fixed?',
    done: 'Task sent back to In progress.',
  },
  block: { label: 'Block', text: 'reason', prompt: 'What is blocking it?', done: 'Task blocked.' },
  unblock: {
    label: 'Unblock',
    text: 'note',
    prompt: 'What changed?',
    done: 'Task unblocked.',
  },
  defer: { label: 'Defer', text: 'reason', prompt: 'Why is it deferred?', done: 'Task deferred.' },
  resume: { label: 'Resume', text: null, prompt: '', done: 'Task resumed.' },
  reopen: {
    label: 'Reopen',
    text: 'reason',
    prompt: 'Why does it need more work?',
    done: 'Task reopened.',
  },
};

export function isUserAction(action: string): action is UserTaskAction {
  return Object.hasOwn(ACTION_INFO, action);
}

const stepText = (no: number): string => `step ${String(no)}`;

const editLabel = (write: Extract<TaskWrite, { kind: 'edit' }>): string =>
  write.edit.percentOverride === undefined ? 'Save the update' : 'Change the percent done';

export function writeDoneText(write: TaskWrite): string {
  switch (write.kind) {
    case 'tick':
      return write.ticked
        ? `Step ${String(write.stepNo)} ticked.`
        : `Step ${String(write.stepNo)} unticked.`;
    case 'assign':
      return 'Assignees saved.';
    case 'assign-step':
      return `Assignees of ${stepText(write.stepNo)} saved.`;
    case 'replan':
      return 'Dates saved.';
    case 'edit':
      return write.edit.percentOverride === undefined ? 'Update saved.' : 'Percent done saved.';
    case 'action':
      return ACTION_INFO[write.action].done;
  }
}

export function writeLabel(write: TaskWrite): string {
  switch (write.kind) {
    case 'tick':
      return `${write.ticked ? 'Tick' : 'Untick'} ${stepText(write.stepNo)}`;
    case 'assign':
      return 'Assign people';
    case 'assign-step':
      return `Assign ${stepText(write.stepNo)}`;
    case 'replan':
      return 'Change dates';
    case 'edit':
      return editLabel(write);
    case 'action':
      return ACTION_INFO[write.action].label;
  }
}
